import { useParams } from 'react-router-dom';

import { Calculator, Printer, Receipt } from 'lucide-react';

import { formatarData } from '@/nucleo/formato/datas';
import { cn } from '@/nucleo/utilitarios';
import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { DetailHeader } from '@/ui/DetailHeader';
import { ErrorState } from '@/ui/ErrorState';
import { MoneyDisplay } from '@/ui/MoneyDisplay';
import { Button } from '@/ui/base/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/ui/base/card';
import { CardSkeleton } from '@/ui/base/table-skeleton';

import { SELO_DA_MEDICAO, formatarCompetenciaPorExtenso } from './apresentacao';
import { useMedicaoDetalhe } from './dados';
import type { ItemDedutivo, ItemMemoriaDeCalculo } from './contrato';

/**
 * `/credenciado/financeiro/:id` — demonstrativo formal de uma medição.
 *
 * Valor bruto, cada dedutivo com sua descrição e valor, o valor líquido, e a
 * memória de cálculo que compôs o bruto — a mesma leitura de um
 * demonstrativo de pagamento em papel. Dado mockado — ver `dados.ts` e
 * `comum/mock.ts` para a justificativa.
 */
export function MedicaoDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { data: medicao, isLoading, isError, refetch } = useMedicaoDetalhe(id);

  if (isError) {
    return (
      <ErrorState
        variant="load-failed"
        description="Não foi possível carregar este demonstrativo agora. Tente novamente em instantes."
        primaryAction={{ label: 'Tentar de novo', onClick: () => void refetch() }}
      />
    );
  }

  if (isLoading) {
    return (
      <div role="status" aria-live="polite" className="space-y-4">
        <CardSkeleton />
        <CardSkeleton />
        <p className="sr-only">Carregando o demonstrativo.</p>
      </div>
    );
  }

  if (!medicao) {
    return (
      <ErrorState
        variant="resource-not-found"
        title="Demonstrativo não encontrado"
        description="Esta medição pode ter sido removida ou o endereço está incorreto."
        primaryAction={{ label: 'Voltar ao financeiro', to: '/credenciado/financeiro' }}
      />
    );
  }

  const selo = SELO_DA_MEDICAO[medicao.status];

  const colunasDaMemoria: DataTableColumn<ItemMemoriaDeCalculo & { id: string }>[] = [
    { id: 'descricao', header: 'Descrição', accessorKey: 'descricao' },
    {
      id: 'quantidade',
      header: 'Quantidade',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (item) => <span className="font-mono text-sm">{item.quantidade}</span>,
    },
    {
      id: 'valor_unitario',
      header: 'Valor unitário',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (item) => <MoneyDisplay value={item.valor_unitario} />,
    },
    {
      id: 'valor_total',
      header: 'Valor total',
      headerClassName: 'text-right',
      cellClassName: 'text-right',
      cell: (item) => <MoneyDisplay value={item.valor_total} className="font-semibold" />,
    },
  ];

  const memoriaComId = medicao.memoria_de_calculo.map((item, indice) => ({
    ...item,
    id: `${medicao.id}-memoria-${indice}`,
  }));

  return (
    <div className="space-y-6">
      <div className="print:hidden">
        <DetailHeader
          backTo="/credenciado/financeiro"
          backLabel="Financeiro"
          title={formatarCompetenciaPorExtenso(medicao.competencia)}
          subtitle={
            medicao.previsao_credito_em
              ? `Previsão de crédito: ${formatarData(medicao.previsao_credito_em)}`
              : 'Ainda sem previsão de crédito'
          }
          status={{ variant: selo.variante, label: selo.rotulo }}
          actions={
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.print()}
              aria-label="Imprimir demonstrativo"
            >
              <Printer className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Imprimir
            </Button>
          }
        />
      </div>

      {/* Cabeçalho só de impressão — mantém a identidade do demonstrativo no papel/PDF gerado pelo navegador. */}
      <div className="hidden print:block">
        <p className="text-xs uppercase tracking-wider text-muted-foreground">
          Demonstrativo de pagamento
        </p>
        <h1 className="mt-1 text-2xl font-bold">
          {formatarCompetenciaPorExtenso(medicao.competencia)}
        </h1>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Receipt className="h-4 w-4" aria-hidden="true" />
            Demonstrativo de pagamento
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-1">
          <LinhaDoResumo rotulo="Valor bruto" valor={medicao.valor_bruto} />

          <div className="pt-3">
            <p className="mb-1 text-xs font-medium uppercase tracking-wider text-muted-foreground">
              Dedutivos
            </p>
            {medicao.dedutivos.length === 0 ? (
              <p className="py-2 text-sm text-muted-foreground">Nenhuma dedução aplicada.</p>
            ) : (
              medicao.dedutivos.map((dedutivo: ItemDedutivo, indice) => (
                <LinhaDoResumo
                  key={`${medicao.id}-dedutivo-${indice}`}
                  rotulo={dedutivo.descricao}
                  valor={dedutivo.valor}
                  negativo
                />
              ))
            )}
            <LinhaDoResumo rotulo="Total de dedutivos" valor={medicao.total_dedutivos} negativo />
          </div>

          <LinhaDoResumo rotulo="Valor líquido a receber" valor={medicao.valor_liquido} destaque />
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Calculator className="h-4 w-4" aria-hidden="true" />
            Memória de cálculo
          </CardTitle>
        </CardHeader>
        <CardContent className="px-0">
          <DataTable
            columns={colunasDaMemoria}
            data={memoriaComId}
            emptyIcon={Calculator}
            emptyTitle="Sem itens calculados"
            emptyDescription="Esta medição ainda não possui itens detalhados."
            minWidth="640px"
            ariaLabel="Memória de cálculo da medição"
          />
        </CardContent>
      </Card>
    </div>
  );
}

function LinhaDoResumo({
  rotulo,
  valor,
  destaque,
  negativo,
}: {
  rotulo: string;
  valor: number;
  destaque?: boolean;
  negativo?: boolean;
}) {
  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 py-2',
        destaque && 'mt-1 border-t pt-3',
      )}
    >
      <span className={cn('text-sm', destaque ? 'font-semibold' : 'text-muted-foreground')}>
        {rotulo}
      </span>
      <span className="inline-flex items-center gap-1">
        {negativo && valor > 0 && <span className="text-sm text-destructive-text">−</span>}
        <MoneyDisplay
          value={valor}
          className={cn(destaque && 'text-base font-bold', negativo && 'text-destructive-text')}
        />
      </span>
    </div>
  );
}

export default MedicaoDetailPage;
